import useStyles from './SidekickDotsIcon.styles';
import type { SidekickIconState } from './SidekickDotsIcon.types';

export type { SidekickIconState } from './SidekickDotsIcon.types';

export type SidekickDotsIconProps = {
  /** Rendered width/height in px. Defaults to 24. */
  size?: number;
  /**
   * Animation state. Defaults to `'standby'`. Renders a static version
   * of the icon when `prefers-reduced-motion: reduce` is active.
   */
  state?: SidekickIconState;
  /** Optional className forwarded to the root `<svg>`. */
  className?: string;
  /**
   * When `true` the icon is treated as decorative and `aria-hidden`.
   * Defaults to `true` because the icon ships inside a container that
   * owns its own accessible label.
   */
  decorative?: boolean;
  /**
   * Accessible name applied as `aria-label` when `decorative={false}`.
   * Defaults to `'Fleet AI Assistant'`. Consumers in localized surfaces
   * can pass a translated string to override the default.
   * Ignored when `decorative={true}` because the SVG is then
   * `aria-hidden` and the parent owns the announcement.
   */
  accessibleName?: string;
};

/**
 * Sidekick brand mark: four dots at the corners of a 24x24 artboard,
 * animated in one of three states. Rest/peak colors come from the
 * theme's `brand[300]` and `brand[600]` tokens (the Figma variables
 * `color/brand/300` and `color/brand/600`) and are animated in lockstep
 * with the dot radius. Honors `prefers-reduced-motion: reduce` by
 * parking all four dots at the peak fill (brand 600).
 *
 * Accessibility wiring (no `<title>` element — `<title>` renders as a
 * native browser tooltip on hover regardless of `aria-hidden`, which
 * leaks an English string into localized UI when the icon ships inside
 * a translated `IconButton` wrapper):
 * - `decorative={true}` (default): `aria-hidden`, no `role`, no
 *   `aria-label`. Biome's `noSvgWithoutTitle` rule treats
 *   `<svg aria-hidden="true">` as valid on its own. The icon ships
 *   inside a labeled container (e.g. a `Box role="img"` or an
 *   `aria-label`-bearing button) that owns the accessible name.
 * - `decorative={false}`: the SVG sets `role="img"` and the consumer's
 *   `accessibleName` (default `'Fleet AI Assistant'`) so AT reliably
 *   announces it as a labeled image across SR/browser combinations. `aria-label`
 *   (rather than `<title>`) is silent for native tooltips. Localized
 *   consumers pass a translated string via `accessibleName`.
 */
function SidekickDotsIcon({
  size = 24,
  state = 'standby',
  className,
  decorative = true,
  accessibleName = 'Fleet AI Assistant',
}: SidekickDotsIconProps) {
  const { classes, cx } = useStyles({ state });
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cx(classes.root, className)}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : accessibleName}
      focusable="false"
      data-state={state}
    >
      <circle cx="5" cy="5" r="1.25" className={classes.dotTL} />
      <circle cx="19" cy="5" r="1.25" className={classes.dotTR} />
      <circle cx="19" cy="19" r="1.25" className={classes.dotBR} />
      <circle cx="5" cy="19" r="1.25" className={classes.dotBL} />
    </svg>
  );
}

export default SidekickDotsIcon;
